import { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { AppLayout } from '@/components/layout/AppLayout';
import { HomePage } from '@/pages/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { ProductPage } from '@/sections/product/ProductPage';
import { ProductReports } from '@/sections/product/ProductReports';
import { ProductBuilder } from '@/sections/product/ProductBuilder';
import { MarketingPage } from '@/sections/marketing/MarketingPage';
import { MarketingReports } from '@/sections/marketing/MarketingReports';
import { MarketingBuilder } from '@/sections/marketing/MarketingBuilder';
import { PushNotificationsPage } from '@/sections/push-notifications/PushNotificationsPage';
import { UsersPage } from '@/sections/users/UsersPage';
import { UsersReports } from '@/sections/users/UsersReports';
import { UsersBuilder } from '@/sections/users/UsersBuilder';
import { PurchasesPage } from '@/sections/purchases/PurchasesPage';
import { PurchasesReports } from '@/sections/purchases/PurchasesReports';
import { PurchasesBuilder } from '@/sections/purchases/PurchasesBuilder';
import { ActivityPage } from '@/sections/activity/ActivityPage';
import { EventFlowPage } from '@/sections/event-flow/EventFlowPage';
import { EventFlowReports } from '@/sections/event-flow/EventFlowReports';
import { EventFlowBuilder } from '@/sections/event-flow/EventFlowBuilder';
import { ScreenFlowPage } from '@/sections/screen-flow/ScreenFlowPage';
import { ScreenFlowReports } from '@/sections/screen-flow/ScreenFlowReports';
import { ScreenFlowBuilder } from '@/sections/screen-flow/ScreenFlowBuilder';
import { AiChatPage } from '@/sections/ai-chat/AiChatPage';

/**
 * Lazy so the injectable SitePing bundle it inlines — several hundred KB of
 * review chrome nobody else needs — stays out of the main chunk.
 */
const ReviewPage = lazy(() => import('@/sections/ai-chat/review/ReviewPage'));

/**
 * Application router.
 *
 * URL scheme:
 *   /                           → Home (section overview)
 *   /product                    → Product reports listing
 *   /product/new                → New product report (builder)
 *   /product/reports/:id        → Saved product report (builder, pre-loaded)
 *   /marketing                  → Marketing reports listing
 *   /marketing/new              → New marketing report (builder)
 *   /marketing/reports/:id      → Saved marketing report (builder, pre-loaded)
 *   /event-flow                 → Event Flow reports listing
 *   /event-flow/new             → New event flow report (builder)
 *   /event-flow/reports/:id     → Saved event flow report (builder, pre-loaded)
 *   /screen-flow                → Screen Flow reports listing
 *   /screen-flow/new            → New screen flow report (builder)
 *   /screen-flow/reports/:id    → Saved screen flow report (builder, pre-loaded)
 *   /users                      → Users reports listing
 *   /users/new                  → New users report (builder)
 *   /users/reports/:id          → Saved users report (builder, pre-loaded)
 *   /ai-chat                    → AI chat (new/last thread)
 *   /ai-chat/:threadId          → AI chat, deep-linked to a specific thread
 *   /review/:threadId?path=…    → HTML review surface for a workspace artifact
 */
export function AppRouter() {
  return (
    <AnimatePresence mode="wait">
      <Routes>
        {/* Standalone, outside AppLayout: no sidebar, no floating chat panel —
            the artifact needs the full viewport to be reviewed honestly. Still
            inside AuthGate (see App.tsx), so the user is authenticated and
            api.get attaches the Firebase token. */}
        <Route
          path="/review/:threadId"
          element={
            <Suspense fallback={<FullPageSpinner />}>
              <ReviewPage />
            </Suspense>
          }
        />

        <Route element={<AppLayout />}>
          {/* Home */}
          <Route path="/" element={<HomePage />} />

          {/* Product */}
          <Route path="/product" element={<ProductPage />}>
            <Route index element={<ProductReports />} />
            <Route path="new" element={<ProductBuilder />} />
            <Route path="reports/:reportId" element={<ProductBuilder />} />
          </Route>

          {/* Marketing */}
          <Route path="/marketing" element={<MarketingPage />}>
            <Route index element={<MarketingReports />} />
            <Route path="new" element={<MarketingBuilder />} />
            <Route path="reports/:reportId" element={<MarketingBuilder />} />
          </Route>

          {/* Event Flow */}
          <Route path="/event-flow" element={<EventFlowPage />}>
            <Route index element={<EventFlowReports />} />
            <Route path="new" element={<EventFlowBuilder />} />
            <Route path="reports/:reportId" element={<EventFlowBuilder />} />
          </Route>

          {/* Screen Flow */}
          <Route path="/screen-flow" element={<ScreenFlowPage />}>
            <Route index element={<ScreenFlowReports />} />
            <Route path="new" element={<ScreenFlowBuilder />} />
            <Route path="reports/:reportId" element={<ScreenFlowBuilder />} />
          </Route>

          {/* Other sections */}
          <Route path="/push-notifications" element={<PushNotificationsPage />} />
          <Route path="/users" element={<UsersPage />}>
            <Route index element={<UsersReports />} />
            <Route path="new" element={<UsersBuilder />} />
            <Route path="reports/:reportId" element={<UsersBuilder />} />
          </Route>
          <Route path="/purchases" element={<PurchasesPage />}>
            <Route index element={<PurchasesReports />} />
            <Route path="new" element={<PurchasesBuilder />} />
            <Route path="reports/:reportId" element={<PurchasesBuilder />} />
          </Route>
          {/* Single route with an optional param so `/ai-chat` and
              `/ai-chat/:threadId` share ONE AiChatPage instance — switching
              the param (e.g. when the backend creates a thread mid-stream)
              updates the URL without remounting the chat runtime. */}
          <Route path="/ai-chat/:threadId?" element={<AiChatPage />} />
          <Route path="/activity" element={<ActivityPage />} />
          <Route path="/settings" element={<ComingSoon section="Settings" />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
}

function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-100">
      <div className="w-6 h-6 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
    </div>
  );
}

function ComingSoon({ section }: { section: string }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">{section}</h2>
        <p className="text-sm text-slate-500">Coming soon</p>
      </div>
    </div>
  );
}
